import React, { useEffect, useMemo, useState } from "react";
import { Select } from "antd";
import { useDispatch } from "react-redux";
import { getEmployeesAPI } from "../../../Redux/Apis/employeeApi";
import { isCeoOrCmoRoleUser } from "../../../app/utils/common/Functions";

const shouldIncludeEmployee = (user, excludeCeoCmo) => {
  if (Number(user?.roleId) === 1) return false;
  if (excludeCeoCmo && isCeoOrCmoRoleUser(user)) return false;
  return true;
};

const mapUsersToOptions = (userData, excludeCeoCmo, { exEmployee = false } = {}) =>
  (Array.isArray(userData) ? userData : [])
    .filter((user) => shouldIncludeEmployee(user, excludeCeoCmo))
    .map((user) => {
      const name = `${user.firstname} ${user.lastname}(${user.role})`;
      return {
        value: user.userId,
        label: exEmployee ? `${name} — Ex-employee` : name,
      };
    });

/** Stable default — inline `[]` in default params is a new reference every render and retriggers fetch effects. */
const EMPTY_EXTRA_OPTIONS = [];

const EmployeeSelect = ({
  value,
  onSelectEmployee = (data) => {},
  width = "250px",
  extraOptions = EMPTY_EXTRA_OPTIONS,
  staticOptions = null,
  placeholder = "Select Employee...",
  disabled = false,
  className = "",
  isMultiple = false,
  role = undefined,
  excludeCeoCmo = true,
  includeExEmployees = false,
  pageSize = 50,
  optionsLoading = false,
  allowClear = true,
}) => {
  const useStaticOptions = Array.isArray(staticOptions);
  const [options, setOptions] = useState([...extraOptions]);
  const [selectedOption, setSelectedOption] = useState(value);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [totalData, setTotalData] = useState(0);
  const dispatch = useDispatch();

  const mergedStaticOptions = useMemo(() => {
    if (!useStaticOptions) return null;
    return staticOptions;
  }, [useStaticOptions, staticOptions]);

  useEffect(() => {
    if (value !== undefined && value !== null) {
      setSelectedOption(value);
    }
  }, [value]);

  useEffect(() => {
    if (useStaticOptions) {
      setOptions(mergedStaticOptions ?? []);
      return;
    }
    setOptions([...extraOptions]);
    setPage(1);
    setTotalData(0);
  }, [useStaticOptions, mergedStaticOptions, extraOptions, excludeCeoCmo, includeExEmployees, role]);

  useEffect(() => {
    if (!includeExEmployees || useStaticOptions || disabled) return;

    let cancelled = false;
    dispatch(getEmployeesAPI({ page: 1, limit: 200, filterBy: "deleted" }))
      .unwrap()
      .then((body) => {
        if (cancelled) return;
        const final = body?.data;
        if (!Array.isArray(final?.userData)) return;
        const exOptions = mapUsersToOptions(final.userData, excludeCeoCmo, { exEmployee: true });
        setOptions((prev) => {
          const activeIds = new Set(
            prev.map((option) => Number(option.value)).filter((id) => Number.isFinite(id) && id > 0)
          );
          const merged = [...prev];
          exOptions.forEach((option) => {
            if (!activeIds.has(Number(option.value))) merged.push(option);
          });
          return merged;
        });
      })
      .catch(() => {});

    return () => {
      cancelled = true;
    };
  }, [includeExEmployees, useStaticOptions, disabled, excludeCeoCmo, dispatch, extraOptions]);

  useEffect(() => {
    if (useStaticOptions || disabled || role) return;

    let cancelled = false;
    setLoading(true);

    dispatch(getEmployeesAPI({ page, limit: pageSize, role }))
      .unwrap()
      .then((body) => {
        if (cancelled) return;
        const final = body?.data;
        if (!final?.pagination || !Array.isArray(final.userData)) {
          setLoading(false);
          return;
        }
        setTotalData(final.pagination.totalData);
        const optData = mapUsersToOptions(final.userData, excludeCeoCmo);
        setOptions((prev) => {
          const exOptions = includeExEmployees
            ? prev.filter((option) => String(option.label || "").includes("Ex-employee"))
            : [];
          return page === 1 ? [...extraOptions, ...optData, ...exOptions] : [...prev, ...optData];
        });
        setLoading(false);
      })
      .catch(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [
    page,
    disabled,
    role,
    excludeCeoCmo,
    useStaticOptions,
    dispatch,
    pageSize,
    extraOptions,
    includeExEmployees,
  ]);

  useEffect(() => {
    if (!role || useStaticOptions || disabled) return;

    let cancelled = false;
    setLoading(true);
    setPage(1);

    dispatch(getEmployeesAPI({ page: 1, limit: pageSize, role }))
      .unwrap()
      .then((body) => {
        if (cancelled) return;
        const final = body?.data;
        if (!final?.pagination || !Array.isArray(final.userData)) {
          setLoading(false);
          return;
        }
        setTotalData(final.pagination.totalData);
        const optData = mapUsersToOptions(final.userData, excludeCeoCmo);
        setOptions([...extraOptions, ...optData]);
        setLoading(false);
      })
      .catch(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [role, excludeCeoCmo, useStaticOptions, disabled, dispatch, pageSize, extraOptions]);

  const handleMenuScrollToBottom = () => {
    if (useStaticOptions || loading || page * pageSize >= totalData) return;
    setPage((prev) => prev + 1);
  };

  const handlePopupScroll = (e) => {
    if (useStaticOptions) return;
    const target = e?.target;
    if (!target) return;
    const nearBottom =
      target.scrollTop + target.clientHeight >= target.scrollHeight - 8;
    if (nearBottom) {
      handleMenuScrollToBottom();
    }
  };

  const selectLoading = optionsLoading || (!useStaticOptions && loading);

  return (
    <Select
      className={`mt-2 employee-select ${className}`}
      style={{ width }}
      mode={isMultiple ? "multiple" : undefined}
      options={options}
      value={selectedOption}
      showSearch
      optionFilterProp="label"
      virtual={false}
      listHeight={320}
      getPopupContainer={() => document.body}
      popupClassName="employee-select-dropdown"
      onChange={(selected) => {
        setSelectedOption(selected);
        if (onSelectEmployee && typeof onSelectEmployee === "function") {
          onSelectEmployee(selected);
        }
      }}
      onPopupScroll={handlePopupScroll}
      loading={selectLoading}
      placeholder={placeholder}
      disabled={disabled}
      allowClear={allowClear}
    />
  );
};

export default EmployeeSelect;
